/**
 * MultiSourcePreview.js
 * 
 * Drop-in replacement for react-player in the Tvadi Playlist Maker.
 * Handles all 7 search sources + any URL from CSV import.
 * 
 * Three rendering modes:
 *   1. <video> tag — for direct media URLs (Wikimedia, NASA, Streamable, etc.)
 *   2. <iframe> — for embed players (YouTube, Vimeo, DM, PeerTube, Archive, TED, etc.)
 *   3. Fallback link — if nothing works, shows a "watch on source" link
 * 
 * Drop into: /var/www/react/frontend/src/new_creatorPlaylist/MultiSourcePreview.js
 */

import React, { useState, useRef, useCallback } from "react";
import {
  getVideoPlatform,
  getPlayerTier,
  getPreviewUrl,
  getPlatformName,
  getPlatformColor,
} from "./videoSourceUtils";

// ══════════════════════════════════════════════════
// STYLES
// ══════════════════════════════════════════════════

const styles = {
  container: {
    position: "relative",
    width: "100%",
    paddingTop: "56.25%", // 16:9 aspect ratio
    backgroundColor: "#0a0a0f",
    borderRadius: "6px",
    overflow: "hidden",
  },
  inner: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
  },
  iframe: {
    width: "100%",
    height: "100%",
    border: "none",
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    backgroundColor: "#000",
  },
  loading: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#0a0a0f",
    zIndex: 5,
  },
  spinner: {
    width: "36px",
    height: "36px",
    border: "3px solid rgba(255,255,255,0.1)",
    borderTop: "3px solid rgba(255,255,255,0.6)",
    borderRadius: "50%",
    animation: "msp-spin 0.8s linear infinite",
  },
  badge: {
    position: "absolute",
    top: "8px",
    left: "8px",
    padding: "3px 8px",
    borderRadius: "3px",
    fontSize: "11px",
    fontWeight: "600",
    color: "#fff",
    letterSpacing: "0.3px",
    zIndex: 10,
    opacity: 0.85,
    pointerEvents: "none",
    fontFamily: "'Geist', 'SF Pro', -apple-system, sans-serif",
  },
  errorContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#0a0a0f",
    gap: "12px",
  },
  errorIcon: {
    fontSize: "32px",
    opacity: 0.4,
  },
  errorText: {
    color: "#888",
    fontSize: "13px",
    fontFamily: "'Geist', -apple-system, sans-serif",
  },
  externalLink: {
    color: "#6ea8fe",
    fontSize: "12px",
    textDecoration: "none",
    padding: "6px 14px",
    border: "1px solid rgba(110,168,254,0.3)",
    borderRadius: "4px",
    fontFamily: "'Geist', -apple-system, sans-serif",
    transition: "border-color 0.15s",
  },
};

// Keyframe animation (injected once)
const SPINNER_CSS_ID = "msp-spinner-css";
function ensureSpinnerCSS() {
  if (typeof document === "undefined") return;
  if (document.getElementById(SPINNER_CSS_ID)) return;
  const style = document.createElement("style");
  style.id = SPINNER_CSS_ID;
  style.textContent = `@keyframes msp-spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(style);
}

// ══════════════════════════════════════════════════
// COMPONENT
// ══════════════════════════════════════════════════

/**
 * MultiSourcePreview
 * 
 * Props:
 *   url       - The original video URL (from playlist data or search result)
 *   embedUrl  - Optional embed URL (_embedUrl from universal search). Takes priority.
 *   embedCode - Optional HTML embed code (from playlist data). Extracts src if present.
 *   title     - Video title (for accessibility)
 *   playing   - Whether to autoplay (default false for preview)
 *   onEnded   - Callback when video ends
 *   onError   - Callback on playback error
 *   showBadge - Show source platform badge (default true)
 *   style     - Override container style
 */
export default function MultiSourcePreview({
  url,
  embedUrl: embedUrlProp,
  embedCode,
  thumbnail: thumbnailProp,
  title = "Video",
  playing = false,
  onEnded,
  onError,
  showBadge = true,
  style: containerStyleOverride,
  className,
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef(null);

  ensureSpinnerCSS();

  // Resolve the effective URL to use
  // Priority: embedUrl prop > src from embedCode > url
  let effectiveUrl = url || "";
  if (embedUrlProp) {
    effectiveUrl = embedUrlProp;
  } else if (embedCode) {
    const trimmed = embedCode.trim();
    // If embedCode is a direct media URL
    if (/^https?:\/\/.*\.(mp4|m3u8|webm|ogg)(\?|#|$)/i.test(trimmed)) {
      effectiveUrl = trimmed;
    } else if (trimmed.includes("discoveryprovider.audius.co") && trimmed.includes("/stream")) {
      // Audius stream URL — use directly as audio source
      effectiveUrl = trimmed;
    } else {
      // Extract src from iframe/video embed code
      const srcMatch = embedCode.match(/src=["']?([^"'\s>]+)["']?/);
      if (srcMatch) effectiveUrl = srcMatch[1];
    }
  }

  // Twitch clips require parent= matching ALL ancestor domains
  if (effectiveUrl && effectiveUrl.includes("clips.twitch.tv")) {
    const currentHost = typeof window !== "undefined" ? window.location.hostname : "tools.tvadi.com";
    // Strip any existing parent= params and rebuild with full chain
    effectiveUrl = effectiveUrl.replace(/[&?]parent=[^&]*/g, "");
    const sep = effectiveUrl.includes("?") ? "&" : "?";
    effectiveUrl = effectiveUrl + sep + "parent=" + currentHost;
    if (currentHost !== "tvadi.com") effectiveUrl += "&parent=tvadi.com";
  }

  const platform = getVideoPlatform(effectiveUrl || url);
  const tier = getPlayerTier(platform, effectiveUrl);
  const previewUrl = getPreviewUrl(effectiveUrl, embedUrlProp);
  const platformName = getPlatformName(platform);
  const platformColor = getPlatformColor(platform);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleError = useCallback((e) => {
    setIsLoading(false);
    setHasError(true);
    if (onError) onError(e);
  }, [onError]);

  const getOuterStyle = () => className
    ? { position: "absolute", top: 0, left: 0, width: "100%", height: "100%", ...containerStyleOverride }
    : { ...styles.container, ...containerStyleOverride };
  const getInnerStyle = () => className
    ? { width: "100%", height: "100%", position: "relative" }
    : styles.inner;

  // ── No URL ──
  if (!effectiveUrl && !url) {
    return (
      <div className={className || undefined} style={getOuterStyle()}>
        <div style={getInnerStyle()}>
          <div style={styles.errorContainer}>
            <div style={styles.errorIcon}>🎬</div>
            <div style={styles.errorText}>No video URL</div>
          </div>
        </div>
      </div>
    );
  }

  // ── Error state ──
  if (hasError) {
    return (
      <div className={className || undefined} style={getOuterStyle()}>
        <div style={getInnerStyle()}>
          <div style={styles.errorContainer}>
            <div style={styles.errorIcon}>⚠</div>
            <div style={styles.errorText}>
              Couldn't load {platformName} video
            </div>
            <a
              href={url || effectiveUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.externalLink}
              onMouseEnter={(e) => e.target.style.borderColor = "rgba(110,168,254,0.6)"}
              onMouseLeave={(e) => e.target.style.borderColor = "rgba(110,168,254,0.3)"}
            >
              Watch on {platformName} ↗
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className || undefined} style={getOuterStyle()}>
      <div style={getInnerStyle()}>

        {/* Source badge */}
        {showBadge && platform !== "unknown" && (
          <div style={{ ...styles.badge, backgroundColor: platformColor }}>
            {platformName}
          </div>
        )}

        {/* Loading overlay — skip for Twitch (IntersectionObserver CSS visibility check blocks autoplay) */}
        {isLoading && platform !== "twitch" && (
          <div style={styles.loading}>
            <div style={styles.spinner} />
            <div style={{
              color: "#555",
              fontSize: "11px",
              marginTop: "10px",
              fontFamily: "'Geist', -apple-system, sans-serif",
            }}>
              Loading {platformName}…
            </div>
          </div>
        )}

        {/* ── Audius: <audio> + artwork overlay (mirrors Home.tsx) ── */}
        {platform === "audius" ? (() => {
          const auThumb = thumbnailProp || "";
          const auStreamUrl = (embedCode && embedCode.includes("/stream")) ? embedCode.trim() : (effectiveUrl && effectiveUrl.includes("/stream")) ? effectiveUrl : previewUrl;
          return (
          <div style={{
            width: "100%", height: "100%",
            position: "relative",
            backgroundColor: "#1a0a2e",
            backgroundImage: auThumb ? "url(" + auThumb + ")" : "none",
            backgroundSize: "cover",
            backgroundPosition: "center",
            overflow: "hidden",
          }}>
            {/* Blur overlay */}
            <div style={{
              position: "absolute",
              top: 0, left: 0, right: 0, bottom: 0,
              backdropFilter: "blur(30px)",
              WebkitBackdropFilter: "blur(30px)",
              backgroundColor: "rgba(0,0,0,0.4)",
              zIndex: 1,
            }} />
            {/* Centered artwork */}
            {auThumb ? (
              <img
                src={auThumb}
                alt={title}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -55%)",
                  maxHeight: "55%",
                  maxWidth: "55%",
                  borderRadius: "8px",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
                  objectFit: "cover",
                  zIndex: 2,
                }}
                onLoad={handleLoad}
              />
            ) : (
              <div style={{
                position: "absolute", top: "50%", left: "50%",
                transform: "translate(-50%, -55%)",
                color: "#CC0FE0", fontSize: "48px", zIndex: 2,
              }}>♫</div>
            )}
            {/* Powered by Audius label */}
            <div style={{
              position: "absolute", top: "10px", right: "10px",
              color: "rgba(255,255,255,0.5)", fontSize: "10px",
              fontFamily: "'Geist', -apple-system, sans-serif",
              zIndex: 3,
            }}>Powered by Audius</div>
            {/* Native audio controls */}
            <audio
              src={auStreamUrl}
              style={{
                position: "absolute", bottom: "8px", left: "5%",
                width: "90%", zIndex: 3,
              }}
              controls
              autoPlay
              onLoadedData={handleLoad}
              onError={handleError}
              onEnded={onEnded}
            />
          </div>
          );
        })()
         : /* ── Tier: Direct <video> ── */
        tier === "video" ? (
          <video
            ref={videoRef}
            src={previewUrl}
            style={styles.video}
            controls
            controlsList="nodownload noremoteplayback"
            preload="metadata"
            autoPlay={true}
            muted // Required for browser autoplay policy
            onLoadedData={handleLoad}
            onError={handleError}
            onEnded={onEnded}
            title={title}
          />
        ) : (
          /* ── Tier: <iframe> embed ── */
          <iframe
            src={previewUrl}
            style={styles.iframe}
            allow="autoplay; encrypted-media; fullscreen"
            allowFullScreen
            title={title}
            onLoad={handleLoad}
            onError={handleError}
            referrerPolicy="no-referrer-when-downgrade"
          />
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════
// NAMED EXPORT: Inline preview for search results
// ══════════════════════════════════════════════════

/**
 * Compact preview for search result cards.
 * Shows a smaller player with click-to-play behavior.
 */
export function SearchResultPreview({ url, embedUrl, title, thumbnail }) {
  const [showPlayer, setShowPlayer] = useState(false);
  const platform = getVideoPlatform(url);
  const platformColor = getPlatformColor(platform);
  const platformName = getPlatformName(platform);

  ensureSpinnerCSS();

  if (!showPlayer) {
    return (
      <div
        onClick={() => setShowPlayer(true)}
        style={{
          position: "relative",
          width: "100%",
          paddingTop: "56.25%",
          backgroundColor: "#111",
          borderRadius: "4px",
          overflow: "hidden",
          cursor: "pointer",
        }}
      >
        {/* Thumbnail */}
        {thumbnail && (
          <img
            src={thumbnail}
            alt={title || "Video thumbnail"}
            style={{
              position: "absolute",
              top: 0, left: 0,
              width: "100%", height: "100%",
              objectFit: "cover",
            }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        )}
        {/* Play button overlay */}
        <div style={{
          position: "absolute",
          top: 0, left: 0,
          width: "100%", height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(0,0,0,0.35)",
          transition: "background 0.15s",
        }}>
          <svg width="48" height="48" viewBox="0 0 48 48">
            <circle cx="24" cy="24" r="22" fill="rgba(0,0,0,0.55)" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"/>
            <polygon points="19,14 19,34 36,24" fill="rgba(255,255,255,0.9)"/>
          </svg>
        </div>
        {/* Platform badge */}
        <div style={{
          position: "absolute",
          bottom: "6px", left: "6px",
          padding: "2px 6px",
          borderRadius: "2px",
          fontSize: "10px",
          fontWeight: "600",
          color: "#fff",
          backgroundColor: platformColor,
          opacity: 0.85,
          fontFamily: "'Geist', -apple-system, sans-serif",
        }}>
          {platformName}
        </div>
      </div>
    );
  }

  return (
    <MultiSourcePreview
      url={url}
      embedUrl={embedUrl}
      title={title}
      playing={true}
      showBadge={true}
    />
  );
}
